import React from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { useConfigValue } from '~/shared/utils/config.js';
import {
  ProductListingFilter as BaseProductListingFilter,
  ProductListingFilterStyle,
} from '~/techstyle-shared/react-components';

import {
  createGetSwatchIcon,
  getFilterStyle,
} from '../../utils/productFilters.js';

const MobileListingFilter = styled(BaseProductListingFilter)`
  ${({ theme }) => theme.gridFilterStyles.listingFilterStyle}

  :last-child {
    box-shadow:
      0 0 0 0 ${({ theme }) => theme.colors.productListingFilter},
      0 -1px 0 0 ${({ theme }) => theme.colors.productListingFilter};
  }
`;

const gridLabelStyle = css`
  position: relative;
  letter-spacing: normal;
  text-align: center;
  padding: 8px 0;

  ${({ theme }) => theme.gridFilterStyles.gridLabelStyle}

  ${({ isChecked }) =>
    isChecked &&
    css`
      color: ${({ theme }) => theme.colors.palette.white};
    `}
`;

const filterFieldLabelStyle = css`
  line-height: 18px;
  font-size: 14px;

  ${({ theme }) => theme.gridFilterStyles.filterFieldLabelStyle}
`;

const filterFieldButtonStyle = css`
  padding: 12px 24px;

  svg {
    margin -5px 0;
    ${({ theme }) => theme.gridFilterStyles.filterFieldButtonSvgStyle}
  }
`;

const swatchLabelStyle = css`
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  z-index: 1;
  padding: 4px 0;
  width: 100%;

  ${({ isChecked }) =>
    isChecked &&
    css`
      color: ${({ theme }) => theme.colors.palette.white};
    `}

  ${({ theme }) => theme.gridFilterStyles.swatchLabelStyle}
`;

const swatchListStyle = css`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-row-gap: 8px;
  grid-column-gap: 8px;
  justify-content: space-between;

  &&& {
    padding: 16px 16px 24px;
  }
`;

const swatchItemStyle = css`
  position: relative;
  justify-content: center;
  width: 100%;
  padding: 0;
`;

const swatchInputStyle = css`
  margin: 0;
  height: 100%;
  width: 100%;
  border: none;
  border-radius: 16px;

  ${({ theme }) => theme.gridFilterStyles.swatchInputStyle}
`;

const circleIconStyle = css`
  border: solid 1px ${({ theme }) => theme.colors.palette.black};
  box-shadow: none;
  height: 22px;
  width: 22px;
  margin-right: 8px;
  display: block;

  ${({ colorSwatches, value, backgroundImage }) =>
    colorSwatches[value] == null &&
    !backgroundImage &&
    css`
      display: none;

      + * {
        margin: auto;
      }
    `};
`;

const swatchInputCheckedStyle = css`
  background-color: ${({ theme }) => theme.colors.palette.black};
`;

const checkmarkStyle = css`
  display: none;
`;

const gridInputCheckedStyle = css`
  background-color: ${({ theme }) => theme.colors.palette.black};
`;

const gridInputStyle = css`
  margin: 0;
  height: 100%;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.palette.white};

  ${({ theme }) => theme.gridFilterStyles.gridInputStyle}
`;

const gridListStyle = css`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  justify-content: space-between;
  grid-row-gap: 8px;

  &&& {
    padding-top: 8px;
    padding-bottom: 24px;
  }
`;

const gridItemStyle = css`
  width: 100%;
  margin: 0;
`;

const itemStyle = css`
  position: relative;
`;

const itemLabelStyle = css`
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: normal;
  color: ${({ theme }) => theme.colors.palette.black};
`;

const listStyle = css`
  &&& {
    padding-left: 16px;
    padding-top: 8px;
  }
`;

const nestedListStyle = css`
  padding: 0 24px;
`;

const nestedHeadingStyle = css`
  line-height: 18px;

  &&& {
    padding-top: 16px;
  }

  ${({ theme }) => theme.gridFilterStyles.nestedHeadingStyle}
`;

const filterCountStyle = css`
  ${({ theme }) => theme.gridFilterStyles.filterCountStyle}
`;

const inputStyle = css`
  ${({ theme }) => theme.gridFilterStyles.inputStyle}
`;

const inputCheckedStyle = css`
  ${({ theme }) => theme.gridFilterStyles.inputCheckedStyle}
`;

const checkmarkCheckedStyle = css`
  color: ${({ theme }) => theme.colors.palette.white};
`;

const checkBoxCheckmarkStyle = css`
  visibility: invisible;
`;

const rootStyle = {
  position: 'absolute',
  inset: 0,
};

const gridCheckboxProps = {
  inputStyle: gridInputStyle,
  inputCheckedStyle: gridInputCheckedStyle,
  rootStyle,
};

const swatchCheckboxProps = {
  checkmarkStyle,
  inputCheckedStyle: swatchInputCheckedStyle,
  inputStyle: swatchInputStyle,
  rootStyle,
};

const checkboxProps = {
  inputStyle,
  inputCheckedStyle,
  checkmarkStyle: checkBoxCheckmarkStyle,
  checkmarkCheckedStyle,
};

function MobileProductListingFilter(props) {
  const imageSwatchFilters = useConfigValue('public.imageSwatchFilters');
  const filterImageCdnUrl = useConfigValue('public.filterImageCdnUrl');
  const { filterField, colorSwatches } = props;
  const facetName = filterField?.field;

  const getSwatchIcon = createGetSwatchIcon({
    facetName,
    colorSwatches,
    imageSwatchFilters,
    filterImageCdnUrl,
    circleIconStyle,
    CircleIconComponent: BaseProductListingFilter.CircleIcon,
  });

  const filterStyle = getFilterStyle(
    facetName,
    imageSwatchFilters,
    ProductListingFilterStyle.SWATCH
  );

  return (
    <MobileListingFilter
      filterFieldLabelStyle={filterFieldLabelStyle}
      filterFieldButtonStyle={filterFieldButtonStyle}
      checkboxProps={checkboxProps}
      circleIconStyle={circleIconStyle}
      filterCountStyle={filterCountStyle}
      getSwatchIcon={getSwatchIcon}
      gridCheckboxProps={gridCheckboxProps}
      itemStyle={itemStyle}
      itemLabelStyle={itemLabelStyle}
      nestedHeadingStyle={nestedHeadingStyle}
      swatchCheckboxProps={swatchCheckboxProps}
      swatchItemStyle={swatchItemStyle}
      swatchLabelStyle={swatchLabelStyle}
      swatchListStyle={swatchListStyle}
      gridListStyle={gridListStyle}
      gridItemStyle={gridItemStyle}
      gridLabelStyle={gridLabelStyle}
      nestedListStyle={nestedListStyle}
      listStyle={listStyle}
      filterStyle={filterStyle}
      {...props}
    />
  );
}

MobileProductListingFilter.propTypes = {
  colorSwatches: PropTypes.object,
  filterField: PropTypes.shape({
    field: PropTypes.string,
  }),
};

export default MobileProductListingFilter;
